/**
 * Propositions libres du joueur (PRD §12, §14.5 ; BACKLOG F6). `estimateMeasure(text, category)` délègue au client
 * de l'API (ui/src/api.ts, ADR 0007) quand `/api/health` répond `ok` avec `estimate: true` ; sinon elle rejette
 * `EstimateUnavailableError` (non transitoire) et l'UI garde la carte d'estimation désactivée avec son libellé d'origine.
 * La proposition est toujours conservée sur l'appareil (localStorage) ; le texte n'est envoyé qu'au service de
 * chiffrage, au clic sur « Estimer », et à l'observatoire seulement après consentement explicite (ui/src/telemetry.ts).
 */
import type { MeasureCategory } from "../../engine/src/types.js";
import { estimateMeasure as apiEstimate, EstimateRateLimitedError, EstimateRefusedError, EstimateUnavailableError, fetchSourcedJob, type EstimatedMeasure, type SourcedJob } from "./api.js";

export { EstimateRateLimitedError, EstimateRefusedError, EstimateUnavailableError };
/** Mesure chiffrée à la volée : aperçu `estimé_ia` ou chiffrage sourcé, et l'objet `Measure` pour le moteur. */
export type { EstimatedMeasure };

/**
 * Mise à jour d'un chiffrage sourcé en cours (ADR 0013) : `est` = version sourcée prête (remplace l'aperçu), sinon
 * `message` = état à afficher (reporté, échec, délai dépassé). Émis sur `document` sous le nom `SOURCED_EVENT`.
 */
export interface SourcedUpdate {
  category: MeasureCategory;
  /** Clé de l'aperçu suivi (la carte n'est remplacée que si elle montre toujours cet aperçu). */
  previewKey: string;
  est?: EstimatedMeasure;
  message?: string;
}
export const SOURCED_EVENT = "bfg:sourced";

type Timer = (fn: () => void, ms: number) => unknown;
const watching = new Set<string>();

/**
 * Suit un job de chiffrage sourcé jusqu'à son terme (délais donnés par le serveur : `poll_after_ms`, `poll_max_ms`).
 * Pas de suivi pour un job reporté au lendemain. `emit` et `timer` injectables (tests hors navigateur).
 */
export function watchSourced(
  est: EstimatedMeasure,
  emit: (u: SourcedUpdate) => void = defaultEmit,
  deps: { timer?: Timer; now?: () => number; fetchJob?: typeof fetchSourcedJob } = {},
): boolean {
  const job = est.job;
  if (!job || est.tier !== "provisional" || job.status === "deferred" || job.status === "done" || job.status === "failed" || watching.has(job.id)) return false;
  const timer = deps.timer ?? ((fn, ms) => setTimeout(fn, ms));
  const now = deps.now ?? (() => Date.now());
  const fetchJob = deps.fetchJob ?? fetchSourcedJob;
  const t0 = now();
  watching.add(job.id);
  const base = { category: est.category, previewKey: est.key };
  const finish = (u: Omit<SourcedUpdate, "category" | "previewKey">) => {
    watching.delete(job.id);
    emit({ ...base, ...u });
  };
  const tick = async () => {
    const r = await fetchJob(job.id, est.text, est.category);
    if (r?.result) return finish({ est: { ...r.result, replaces: est.measure.id, hit_count: Math.max(r.result.hit_count, est.hit_count) } });
    if (r?.refused) return finish({ message: r.refused });
    if (r && (r.job.status === "failed" || r.job.status === "deferred" || r.job.status === "done")) return finish({ message: r.job.message });
    const step: SourcedJob = r?.job ?? job;
    if (now() - t0 + step.poll_after_ms > step.poll_max_ms) return finish({ message: step.message });
    timer(() => void tick(), step.poll_after_ms);
  };
  timer(() => void tick(), job.poll_after_ms);
  return true;
}

function defaultEmit(u: SourcedUpdate) {
  try {
    if (typeof document !== "undefined") document.dispatchEvent(new CustomEvent<SourcedUpdate>(SOURCED_EVENT, { detail: u }));
  } catch {
    /* hors navigateur */
  }
}

/** Chiffre la proposition ; si un chiffrage sourcé est en cours, le suit et émet `SOURCED_EVENT` à son terme. */
export async function estimateMeasure(text: string, category: MeasureCategory): Promise<EstimatedMeasure> {
  const est = await apiEstimate(text, category);
  watchSourced(est);
  return est;
}

/** Proposition conservée localement. */
export interface StoredProposal {
  id: string;
  text: string;
  category: MeasureCategory;
  /** Horodatage ISO de saisie (UI uniquement, pas dans le moteur). */
  created_at: string;
  /** Dictée vocale (true) ou écrite. */
  dictated: boolean;
}

const KEY = "bfg.proposals.v1";

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function listProposals(category?: MeasureCategory): StoredProposal[] {
  const s = storage();
  if (!s) return [];
  try {
    const all = JSON.parse(s.getItem(KEY) ?? "[]") as StoredProposal[];
    return Array.isArray(all) ? all.filter((p) => !category || p.category === category) : [];
  } catch {
    return [];
  }
}

export function saveProposal(text: string, category: MeasureCategory, dictated: boolean): StoredProposal | null {
  const t = text.trim();
  const s = storage();
  if (!t || !s) return null;
  const p: StoredProposal = { id: `p${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`, text: t, category, created_at: new Date().toISOString(), dictated };
  const all = listProposals();
  all.push(p);
  s.setItem(KEY, JSON.stringify(all));
  return p;
}

export function deleteProposal(id: string) {
  const s = storage();
  if (!s) return;
  s.setItem(KEY, JSON.stringify(listProposals().filter((p) => p.id !== id)));
}

/* ------------------------------------------------------------------ dictée (Web Speech API) */

interface SpeechRec {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((ev: { results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((ev: { error: string }) => void) | null;
  start(): void;
  stop(): void;
}

type SpeechCtor = new () => SpeechRec;

export function speechAvailable(): boolean {
  const w = globalThis as unknown as { webkitSpeechRecognition?: SpeechCtor; SpeechRecognition?: SpeechCtor };
  return !!(w.webkitSpeechRecognition ?? w.SpeechRecognition);
}

/** Démarre une dictée fr-FR ; `onText` reçoit la transcription (provisoire puis finale). Renvoie une fonction d'arrêt. */
export function startDictation(onText: (text: string, final: boolean) => void, onEnd: (error?: string) => void): (() => void) | null {
  const w = globalThis as unknown as { webkitSpeechRecognition?: SpeechCtor; SpeechRecognition?: SpeechCtor };
  const Ctor = w.webkitSpeechRecognition ?? w.SpeechRecognition;
  if (!Ctor) return null;
  const rec = new Ctor();
  rec.lang = "fr-FR";
  rec.interimResults = true;
  rec.continuous = false;
  rec.onresult = (ev) => {
    let txt = "";
    let final = false;
    for (let i = 0; i < ev.results.length; i++) {
      const r = ev.results[i]!;
      txt += r[0]?.transcript ?? "";
      final = r.isFinal;
    }
    onText(txt, final);
  };
  rec.onerror = (ev) => onEnd(ev.error);
  rec.onend = () => onEnd();
  rec.start();
  return () => rec.stop();
}
